import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Send, Shield, User, Terminal, RefreshCw, Loader2 } from 'lucide-react';
import axios from 'axios';
import { trackEvent } from '../lib/analytics';
import { useAuth, formatApiErrorDetail } from '../context/AuthContext';
import EntryCard from './EntryCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const MAX_DEPTH = 4;

const buildTree = (flat) => {
  const map = new Map();
  flat.forEach((m) => map.set(m.id, { ...m, children: [] }));
  const roots = [];
  flat.forEach((m) => {
    const node = map.get(m.id);
    if (m.parent_id && map.has(m.parent_id)) {
      map.get(m.parent_id).children.push(node);
    } else {
      roots.push(node);
    }
  });
  // newest roots first
  roots.sort((a, b) => b.timestamp - a.timestamp);
  // oldest replies first within a thread
  const sortRec = (nodes) => {
    nodes.forEach((n) => { n.children.sort((a, b) => a.timestamp - b.timestamp); sortRec(n.children); });
  };
  sortRec(roots);
  return roots;
};

const flattenIds = (nodes, set = new Set()) => {
  nodes.forEach((n) => { set.add(n.id); flattenIds(n.children, set); });
  return set;
};

const matchSearch = (node, term) => {
  if (!term) return true;
  const t = term.toLowerCase();
  if (node.sender.toLowerCase().includes(t)) return true;
  if (node.message.toLowerCase().includes(t)) return true;
  return node.children.some((c) => matchSearch(c, t));
};

export const GuestbookView = () => {
  const { isAuthenticated, isAdmin, user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [name, setName] = useState('');
  const [text, setText] = useState('');
  const [isModerator, setIsModerator] = useState(false);
  const [searchFilter, setSearchFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [activeReplyId, setActiveReplyId] = useState(null);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 5;

  // Auto-fill name from auth context
  useEffect(() => {
    if (isAuthenticated && user) {
      setName(user.full_name || user.email);
    }
  }, [isAuthenticated, user]);

  const fetchEntries = useCallback(async () => {
    setLoading(true);
    setErrorMsg('');
    try {
      const res = await axios.get(`${API}/guestbook`);
      setMessages(res.data || []);
    } catch (err) {
      setErrorMsg(formatApiErrorDetail(err?.response?.data?.detail) || err.message || 'Failed to load ledger.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchEntries(); }, [fetchEntries, isAuthenticated]);

  const handlePublish = async (e) => {
    e.preventDefault();
    if (!name.trim() || !text.trim() || submitting) return;
    setSubmitting(true);
    setErrorMsg('');
    try {
      const res = await axios.post(`${API}/guestbook`, {
        sender: name.trim(),
        message: text.trim(),
      });
      setMessages([...messages, res.data]);
      // Re-fill the name from the auth context for logged-in users so they can
      // post again immediately (otherwise the read-only input goes blank).
      if (isAuthenticated && user) {
        setName(user.full_name || user.email);
      } else {
        setName('');
      }
      setText('');
      trackEvent('guestbook_publish', { sender: res.data.sender });
    } catch (err) {
      setErrorMsg(formatApiErrorDetail(err?.response?.data?.detail) || err.message || 'Transmission failed.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReplySubmit = async ({ sender, message, parent_id }) => {
    const res = await axios.post(`${API}/guestbook`, { sender, message, parent_id });
    setMessages((prev) => [...prev, res.data]);
    trackEvent('guestbook_reply', { parent: parent_id });
  };

  const handleDelete = async (id) => {
    if (!isAuthenticated) {
      setErrorMsg('You must be signed in to moderate.');
      return;
    }
    try {
      await axios.delete(`${API}/guestbook/${id}`);
      // also remove its descendants client-side
      const tree = buildTree(messages);
      const find = (nodes) => {
        for (const n of nodes) { if (n.id === id) return n; const f = find(n.children); if (f) return f; }
        return null;
      };
      const target = find(tree);
      const ids = target ? flattenIds([target]) : new Set([id]);
      setMessages(messages.filter((m) => !ids.has(m.id)));
      trackEvent('guestbook_delete', { id });
    } catch (err) {
      setErrorMsg(formatApiErrorDetail(err?.response?.data?.detail) || err.message || 'Delete failed.');
    }
  };

  const handleResetFeed = async () => {
    if (!isAuthenticated) {
      setErrorMsg('You must be signed in to moderate.');
      return;
    }
    if (!window.confirm('RESET TRANSMISSIONS: Restore default guestbook signals (all replies will be wiped)?')) return;
    try {
      await axios.post(`${API}/guestbook/reset`);
      await fetchEntries();
      trackEvent('guestbook_reset');
    } catch (err) {
      setErrorMsg(formatApiErrorDetail(err?.response?.data?.detail) || err.message || 'Reset failed.');
    }
  };

  const handleEdit = async (id, message) => {
    const res = await axios.patch(`${API}/guestbook/${id}`, { message });
    setMessages((prev) => prev.map((m) => (m.id === id ? { ...m, ...res.data } : m)));
  };

  const tree = useMemo(() => buildTree(messages), [messages]);
  const filteredTree = useMemo(
    () => tree.filter((n) => matchSearch(n, searchFilter)),
    [tree, searchFilter]
  );
  const totalPages = Math.max(1, Math.ceil(filteredTree.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pagedTree = useMemo(
    () => filteredTree.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE),
    [filteredTree, safePage]
  );

  // Reset to page 1 when the filter changes or messages count drops below current page.
  useEffect(() => { setPage(1); }, [searchFilter]);

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-16" data-testid="guestbook-view">
      <section className="space-y-4">
        <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#39ff14]/10 border border-[#39ff14]/20 rounded-full">
          <Terminal className="w-3 h-3 text-[#39ff14] shrink-0" />
          <span className="font-mono text-[10px] text-[#39ff14] tracking-widest font-semibold uppercase">04 // PUBLIC LEDGER MATRIX</span>
        </div>
        <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-4">
          <div className="max-w-2xl space-y-2">
            <h2 className="font-display font-black text-3xl sm:text-4xl text-white tracking-tight uppercase">The Guestbook (Transmissions)</h2>
            <p className="text-slate-400 text-xs sm:text-sm font-light leading-relaxed">
              Broadcast encrypted telemetry, reply to other operators, or thread questions on this shared MongoDB-backed ledger. Replies nest up to {MAX_DEPTH} levels.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {isAdmin && (
              <>
                <button
                  onClick={() => setIsModerator(!isModerator)}
                  className={`px-3 py-1.5 rounded font-mono text-[10px] font-bold tracking-wide border flex items-center gap-1.5 transition-all cursor-pointer ${
                    isModerator ? 'bg-rose-500/15 text-rose-400 border-rose-500/35' : 'bg-slate-900 text-slate-400 border-slate-800'
                  }`}
                  data-testid="toggle-moderator"
                >
                  <Shield className="w-3 h-3" />
                  <span>MODERATOR MODE: {isModerator ? 'ON' : 'OFF'}</span>
                </button>
                <button
                  onClick={handleResetFeed}
                  className="px-3 py-1.5 bg-slate-900 hover:bg-slate-800 text-slate-500 hover:text-slate-200 border border-slate-800 font-mono text-[10px] font-bold tracking-wide rounded flex items-center gap-1.5 transition-all cursor-pointer"
                  data-testid="btn-restore"
                >
                  <RefreshCw className="w-3 h-3" />
                  <span>RESEED</span>
                </button>
              </>
            )}
          </div>
        </div>
      </section>

      <section className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
        <div className="lg:col-span-5 space-y-6">
          <div className="p-6 glass-card rounded-xl border border-slate-800/80 space-y-5 relative">
            <div className="flex items-center gap-2.5">
              <Terminal className="w-4 h-4 text-emerald-400" />
              <h4 className="font-display font-semibold text-xs text-white uppercase tracking-wider">Initiate Broadcast Transmission</h4>
            </div>
            <form onSubmit={handlePublish} className="space-y-4">
              <div className="space-y-1.5">
                <label className="font-mono text-[9px] text-[#39ff14] uppercase block font-semibold tracking-wider">
                  01 / Identifier (Name) {isAuthenticated && <span className="text-slate-500">· auto-filled from your account</span>}
                </label>
                <div className="relative">
                  <User className="absolute left-3.5 top-3 w-4 h-4 text-slate-500" />
                  <input
                    type="text"
                    required
                    maxLength={32}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="E.g., Agent_Rios"
                    readOnly={isAuthenticated}
                    className={`w-full pl-10 pr-4 py-2.5 bg-slate-950 border border-slate-850 font-mono text-xs rounded focus:outline-none focus:border-[#00f0ff] focus:ring-1 focus:ring-[#00f0ff]/50 transition-all text-white placeholder:text-slate-600 ${isAuthenticated ? 'cursor-not-allowed opacity-90' : ''}`}
                    data-testid="input-guestbook-sender"
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <label className="font-mono text-[9px] text-[#39ff14] uppercase block font-semibold tracking-wider">02 / Transmission Message</label>
                <textarea required maxLength={256} value={text} onChange={(e) => setText(e.target.value)} placeholder="Record credentials footprint or platform diagnostic..." rows={4} className="w-full px-4 py-3 bg-slate-950 border border-slate-850 font-sans text-xs rounded focus:outline-none focus:border-[#00f0ff] focus:ring-1 focus:ring-[#00f0ff]/50 transition-all text-white placeholder:text-slate-600 resize-none font-light leading-relaxed" data-testid="input-guestbook-message" />
              </div>
              <button type="submit" disabled={submitting || !name.trim() || !text.trim()} className="w-full py-3 bg-[#39ff14] hover:bg-[#32e010] disabled:opacity-40 text-slate-950 font-display font-black text-xs tracking-widest uppercase rounded cursor-pointer transition-all flex items-center justify-center gap-2" data-testid="btn-publish">
                {submitting ? (<><Loader2 className="w-3.5 h-3.5 animate-spin" /><span>BROADCASTING...</span></>) : (<><Send className="w-3.5 h-3.5" /><span>PUBLISH TO FEED</span></>)}
              </button>
              {errorMsg && (
                <div className="text-[10px] font-mono text-rose-400 bg-rose-500/5 border border-rose-500/20 rounded p-2.5" data-testid="guestbook-error">
                  ! {errorMsg}
                </div>
              )}
            </form>
            <div className="rounded border border-slate-900 bg-slate-950/60 p-4 font-mono text-[9px] text-slate-500 leading-relaxed text-center">
              MONGODB SHARED LEDGER · 30s/IP RATE-LIMIT · NESTED REPLIES SUPPORTED
            </div>
            {!isAuthenticated && (
              <div className="rounded border border-amber-500/20 bg-amber-500/5 p-3 font-mono text-[9px] text-amber-300 leading-relaxed text-center">
                Anonymous viewers see masked IPs. <span className="text-slate-400">Sign in to reveal full IPs.</span>
              </div>
            )}
          </div>
        </div>

        <div className="lg:col-span-7 space-y-4">
          <div className="flex gap-4 p-1 rounded-lg border border-slate-900 bg-slate-950/60">
            <input type="text" value={searchFilter} onChange={(e) => setSearchFilter(e.target.value)} placeholder="Filter transmission hashes or contents..." className="w-full px-4 py-2 bg-transparent focus:outline-none font-mono text-xs text-slate-300 placeholder:text-slate-600" data-testid="input-search-guestbook" />
            {searchFilter && (
              <button onClick={() => setSearchFilter('')} className="font-mono text-[9px] text-slate-500 hover:text-white px-3 border-l border-slate-900">RESET</button>
            )}
          </div>

          {loading ? (
            <div className="p-8 border border-dashed border-slate-850 rounded text-center text-slate-500 font-mono text-xs flex items-center justify-center gap-2" data-testid="guestbook-loading">
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
              <span>SYNCING LEDGER FROM MONGO NODE...</span>
            </div>
          ) : (
            <div className="space-y-4" data-testid="guestbook-list">
              <AnimatePresence initial={false}>
                {filteredTree.length === 0 ? (
                  <div className="p-8 border border-dashed border-slate-850 rounded text-center text-slate-500 font-mono text-xs">
                    Zero active footprints detected inside ledger matching state.
                  </div>
                ) : (
                  pagedTree.map((root) => (
                    <EntryCard
                      key={root.id}
                      node={root}
                      depth={0}
                      isAuthenticated={isAuthenticated}
                      isAdmin={isAdmin}
                      currentUserId={user?.id}
                      isModerator={isModerator}
                      onDelete={handleDelete}
                      onReplySubmit={handleReplySubmit}
                      onEdit={handleEdit}
                      activeReplyId={activeReplyId}
                      setActiveReplyId={setActiveReplyId}
                    />
                  ))
                )}
              </AnimatePresence>
            </div>
          )}

          {!loading && filteredTree.length > 0 && (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 mt-4 pt-3 border-t border-slate-900/60" data-testid="guestbook-pagination">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={safePage === 1}
                className="px-3 py-1.5 rounded border border-slate-800 bg-slate-900/60 text-slate-300 hover:bg-slate-800 disabled:opacity-30 disabled:cursor-not-allowed font-mono text-[10px] uppercase tracking-widest font-bold flex items-center gap-1.5 transition-all cursor-pointer"
                data-testid="btn-page-prev"
              >
                ← Previous
              </button>
              <div className="font-mono text-[10px] text-slate-500 uppercase tracking-widest text-center" data-testid="page-indicator">
                Page <span className="text-[#00f0ff]">{safePage}</span> / {totalPages}
                <span className="text-slate-600 ml-2">· {filteredTree.length} thread{filteredTree.length === 1 ? '' : 's'} · {PAGE_SIZE} per page</span>
              </div>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={safePage === totalPages}
                className="px-3 py-1.5 rounded border border-slate-800 bg-slate-900/60 text-slate-300 hover:bg-slate-800 disabled:opacity-30 disabled:cursor-not-allowed font-mono text-[10px] uppercase tracking-widest font-bold flex items-center gap-1.5 transition-all cursor-pointer"
                data-testid="btn-page-next"
              >
                Next →
              </button>
            </div>
          )}
          <div className="font-mono text-[8px] text-slate-500 text-center uppercase tracking-widest py-2">
            Shared MongoDB ledger · {messages.length} total transmissions · {isAuthenticated ? 'authenticated view (full IPs)' : 'anonymous view (masked IPs)'}
          </div>
        </div>
      </section>
    </motion.div>
  );
};
